// src/components/Settings/SettingsModal.tsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ChangePassword } from './ChangePassword';
import { Backup } from './Backup';
import { WebAuthnSetup } from './WebAuthnSetup';  // ✅ 加回

interface SettingsModalProps {
  isOpen: boolean;
  userId: string;
  username: string;
  masterPassword: string;
  currentDEK: CryptoKey | null;
  decryptedDEK: CryptoKey | null;
  onClose: () => void;
  onPasswordChanged: () => void;
  onImportSuccess?: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  userId,
  username,
  masterPassword,
  currentDEK,
  decryptedDEK,
  onClose,
  onPasswordChanged,
  onImportSuccess,
}) => {
  // ✅ 加回 'fingerprint'
  const [activeTab, setActiveTab] = useState<'password' | 'backup' | 'fingerprint'>('password');

  if (!isOpen) return null;

  const handlePasswordSuccess = () => {
    onPasswordChanged();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* 標題欄 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">設定</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 使用者資訊 */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
              <span className="text-blue-600 dark:text-blue-400 font-semibold">
                {username.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <div className="font-medium text-gray-900 dark:text-white truncate">{username}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">已登入</div>
            </div>
          </div>
        </div>

        {/* ✅ Tab 區域 - 三個 Tab */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto flex-shrink-0">
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-shrink-0 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'password'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            🔐 修改密碼
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`flex-shrink-0 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'backup'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            💾 備份與還原
          </button>
          {/* ✅ 加回指紋登錄 Tab */}
          <button
            onClick={() => setActiveTab('fingerprint')}
            className={`flex-shrink-0 px-3 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'fingerprint'
                ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            👆 指紋登錄
          </button>
        </div>

        {/* ✅ 內容區域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'password' && (
            <ChangePassword
              userId={userId}
              onSuccess={handlePasswordSuccess}
              onCancel={onClose}
            />
          )}
          {activeTab === 'backup' && (
            <Backup
              userId={userId}
              username={username}
              password={masterPassword}
              currentDEK={currentDEK}
              onImportSuccess={onImportSuccess}
              onClose={onClose}
            />
          )}
          {/* ✅ 加回指紋登錄內容 */}
          {activeTab === 'fingerprint' && (
            <WebAuthnSetup
              userId={userId}
              username={username}
              decryptedDEK={decryptedDEK}
            />
          )}
        </div>
      </div>
    </div>
  );
};